import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { useFeatureFlag } from '../lib/featureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AIChatbot = () => {
  const enabled = useFeatureFlag('ai_chatbot');
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m PanasheBot. Ask me about routes, luggage, or travel requirements.' }
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('panashe_chat_session') || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/public/ai/chat`, {
        session_id: sessionId,
        message: text,
      });
      const reply = res.data?.reply || 'Sorry, I had trouble understanding that.';
      if (res.data?.session_id && res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
        localStorage.setItem('panashe_chat_session', res.data.session_id);
      }
      setMessages(m => [...m, { role: 'assistant', content: reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: 'Sorry, I\'m offline. Please call +263 786 563 430.' }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!enabled) return null;

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-chatbot-launcher"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-xl hover:scale-105 transition-transform flex items-center justify-center"
          aria-label="Open chat assistant"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-chatbot-panel"
          className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              <div>
                <p className="font-semibold text-sm">PanasheBot</p>
                <p className="text-xs opacity-80">AI Travel Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ai-chatbot-close"
              className="p-1 hover:bg-white/10 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50"
            data-testid="ai-chatbot-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-violet-600 text-white rounded-br-sm'
                      : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white text-slate-500 border border-slate-200 px-3 py-2 rounded-2xl rounded-bl-sm text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="p-2 border-t bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about routes, luggage, requirements..."
                data-testid="ai-chatbot-input"
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-full focus:outline-none focus:border-violet-500"
                disabled={sending}
              />
              <button
                onClick={send}
                disabled={sending || !input.trim()}
                data-testid="ai-chatbot-send"
                className="w-9 h-9 rounded-full bg-violet-600 text-white flex items-center justify-center disabled:opacity-40 hover:bg-violet-700"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 text-center mt-1">Powered by Claude · Panashe Coaches</p>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;
